'use client';

import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';

import professionalsService from '@/services/professionals-services';
import type {
  ClaimDemandSignalResponse,
  InitiateClaimResponse,
  ProfessionalRead,
} from '@/types/professionals-types';

type ClaimStatus = 'idle' | 'loading' | 'success' | 'error' | 'third_party';

interface UseClaimInitiateReturn {
  professional: ProfessionalRead | null;
  loadingProfessional: boolean;
  loadError: string | null;
  /** Public per-lawyer demand signal for the landing hero (#5369). `null`
      until loaded or on any failure — the hero falls back to qualitative
      copy, never a bare 0 at the conversion moment. */
  demandSignal: ClaimDemandSignalResponse | null;
  claimStatus: ClaimStatus;
  claimResponse: InitiateClaimResponse | null;
  initiateError: string | null;
  triggerEmailClaim: () => Promise<void>;
  /** Self-declaration gate (#8306): the visitor confirmed they are NOT the
      professional. Logs the declaration server-side and moves to the
      'third_party' status — never sends the verification email. */
  declineAndNotifyThirdParty: () => Promise<void>;
}

/**
 * Claim-page state machine: fetches the professional on mount, exposes a
 * `triggerEmailClaim` action + the current claim status.
 *
 * Extracted so the page component stays presentational. The axios interceptor
 * returns the response body (not the ApiRespV2 wrapper) — see api-services.ts
 * — so we type the `initiateEmailClaim` return as the raw body shape.
 *
 * `pendingInquiryId` is threaded through to the backend so the post-verify
 * redirect lands on the inquiry-thread that brought the lawyer here (cold-
 * outreach flow). When undefined the claim flow behaves identically to the
 * organic path.
 */
export function useClaimInitiate(
  professionalId: string,
  pendingInquiryId: string | null = null
): UseClaimInitiateReturn {
  const t = useTranslations('Lawyers.claim');

  const [professional, setProfessional] = useState<ProfessionalRead | null>(
    null
  );
  const [loadingProfessional, setLoadingProfessional] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [demandSignal, setDemandSignal] =
    useState<ClaimDemandSignalResponse | null>(null);

  const [claimStatus, setClaimStatus] = useState<ClaimStatus>('idle');
  const [claimResponse, setClaimResponse] =
    useState<InitiateClaimResponse | null>(null);
  const [initiateError, setInitiateError] = useState<string | null>(null);

  useEffect(() => {
    if (!professionalId) return;
    async function load() {
      try {
        const response =
          await professionalsService.getProfessional(professionalId);
        if (response.data) {
          setProfessional(response.data);
        } else {
          setLoadError(t('professionalNotFound'));
        }
      } catch {
        setLoadError(t('loadError'));
      } finally {
        setLoadingProfessional(false);
      }
    }
    // Best-effort demand signal (#5369) — runs in parallel with the
    // profile load. A failure leaves `demandSignal` null and the hero
    // falls back to qualitative copy; it never blocks the claim flow.
    async function loadDemandSignal() {
      try {
        const response =
          await professionalsService.getClaimDemandSignal(professionalId);
        if (response.data) setDemandSignal(response.data);
      } catch {
        // Swallow — qualitative fallback in the hero covers this.
      }
    }
    load();
    loadDemandSignal();
  }, [professionalId, t]);

  async function triggerEmailClaim(): Promise<void> {
    if (!professional) return;
    setClaimStatus('loading');
    setInitiateError(null);
    try {
      const response = (await professionalsService.initiateEmailClaim({
        professional_id: professional.id,
        method: 'email',
        pending_inquiry_id: pendingInquiryId,
        declared_role: 'self',
      })) as unknown as InitiateClaimResponse;

      setClaimResponse(response);
      if (response.success) {
        setClaimStatus('success');
      } else {
        setClaimStatus('error');
        setInitiateError(response.error || t('claimError'));
      }
    } catch {
      setClaimStatus('error');
      setInitiateError(t('claimError'));
    }
  }

  async function declineAndNotifyThirdParty(): Promise<void> {
    if (!professional) return;
    setClaimStatus('loading');
    setInitiateError(null);
    try {
      const response = (await professionalsService.initiateEmailClaim({
        professional_id: professional.id,
        method: 'email',
        pending_inquiry_id: pendingInquiryId,
        declared_role: 'third_party',
      })) as unknown as InitiateClaimResponse;

      setClaimResponse(response);
      if (response.success) {
        setClaimStatus('third_party');
      } else {
        setClaimStatus('error');
        setInitiateError(response.error || t('claimError'));
      }
    } catch {
      setClaimStatus('error');
      setInitiateError(t('claimError'));
    }
  }

  return {
    professional,
    loadingProfessional,
    loadError,
    demandSignal,
    claimStatus,
    claimResponse,
    initiateError,
    triggerEmailClaim,
    declineAndNotifyThirdParty,
  };
}
